import React, { useState, useEffect, useCallback } from "react";
import {
  Trash2, RotateCcw, AlertTriangle, Video, FileText,
  BookOpen, HardDrive, Loader, CheckCircle,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./trash.scss";

// Three entity KINDS — categories, so the chart ramp. `token` rather than a
// colour because the badge tint was `meta.color + "18"`, a hex concatenation.
const TYPE_META = {
  video: { label: "Video", icon: Video, token: "--chart-5" },
  material: { label: "Material", icon: FileText, token: "--chart-2" },
  lesson: { label: "Lesson", icon: BookOpen, token: "--chart-1" },
};

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function timeAgo(date) {
  const diff = Date.now() - new Date(date).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
}

const TrashPage = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [actionLoading, setActionLoading] = useState(null); // item id
  const [emptyLoading, setEmptyLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const fetchTrash = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/trash");
      setItems(data.items || []);
      setTotalSize(data.totalSize || 0);
    } catch {
      /* ignore */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTrash();
  }, [fetchTrash]);

  const showToast = (msg, variant = "success") => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3000);
  };

  const handleRestore = async (item) => {
    setActionLoading(item._id);
    try {
      await apiClient.post(`/trash/${item._id}/restore`, { type: item.type });
      showToast(`${item.title} restored`);
      setItems((prev) => prev.filter((i) => i._id !== item._id));
      setTotalSize((prev) => prev - (item.fileSize || 0));
      setSelected((prev) => { const n = new Set(prev); n.delete(item._id); return n; });
    } catch {
      showToast("Restore failed", "error");
    }
    setActionLoading(null);
  };

  const handlePurge = async (item) => {
    setActionLoading(item._id);
    setConfirmPurge(null);
    try {
      await apiClient.delete(`/trash/${item._id}`, { data: { type: item.type } });
      showToast(`${item.title} permanently deleted`);
      setItems((prev) => prev.filter((i) => i._id !== item._id));
      setTotalSize((prev) => prev - (item.fileSize || 0));
      setSelected((prev) => { const n = new Set(prev); n.delete(item._id); return n; });
    } catch {
      showToast("Delete failed", "error");
    }
    setActionLoading(null);
  };

  const handleEmptyTrash = async () => {
    setEmptyLoading(true);
    setConfirmEmpty(false);
    try {
      const { data } = await apiClient.delete("/trash/empty");
      showToast(data.message || "Trash emptied");
      setItems([]);
      setTotalSize(0);
      setSelected(new Set());
    } catch {
      showToast("Failed to empty trash", "error");
    }
    setEmptyLoading(false);
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i._id)));
  };

  const handleBulkRestore = async () => {
    for (const item of items.filter((i) => selected.has(i._id))) {
      await handleRestore(item);
    }
  };

  const handleBulkPurge = async () => {
    for (const item of items.filter((i) => selected.has(i._id))) {
      await handlePurge(item);
    }
  };

  if (loading) {
    return (
      <div className="trash-page">
        <div className="trash-loading">
          <Loader className="spin" size={24} />
          <span>Loading trash…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="trash-page">
      {/* Toast */}
      {toast && (
        <div className={`trash-toast trash-toast--${toast.variant}`}>
          {toast.variant === "success" ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="trash-header">
        <div className="trash-header__left">
          <Trash2 size={24} />
          <div>
            <h1>Trash</h1>
            <p>
              {items.length} item{items.length !== 1 ? "s" : ""} &middot;{" "}
              {formatBytes(totalSize)} recoverable storage
            </p>
          </div>
        </div>
        <div className="trash-header__actions">
          {items.length > 0 && (
            <button
              className="trash-btn trash-btn--danger"
              onClick={() => setConfirmEmpty(true)}
              disabled={emptyLoading}
            >
              {emptyLoading ? <Loader className="spin" size={14} /> : <Trash2 size={14} />}
              Empty Trash
            </button>
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="trash-bulk">
          <span>{selected.size} selected</span>
          <button className="trash-btn trash-btn--outline" onClick={handleBulkRestore}>
            <RotateCcw size={14} /> Restore
          </button>
          <button className="trash-btn trash-btn--danger-outline" onClick={handleBulkPurge}>
            <Trash2 size={14} /> Delete permanently
          </button>
        </div>
      )}

      {/* Empty State */}
      {items.length === 0 && (
        <div className="trash-empty">
          <HardDrive size={48} strokeWidth={1} />
          <h3>Trash is empty</h3>
          <p>Deleted videos, materials, and lessons will appear here.</p>
        </div>
      )}

      {/* Items */}
      {items.length > 0 && (
        <div className="trash-list">
          <div className="trash-list__header">
            <label className="trash-check">
              <input
                type="checkbox"
                checked={selected.size === items.length}
                onChange={toggleAll}
              />
            </label>
            <span className="trash-col--name">Name</span>
            <span className="trash-col--type">Type</span>
            <span className="trash-col--size">Size</span>
            <span className="trash-col--date">Deleted</span>
            <span className="trash-col--actions">Actions</span>
          </div>

          {items.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.material;
            const Icon = meta.icon;
            const isActing = actionLoading === item._id;

            return (
              <div
                key={item._id}
                className={`trash-item${selected.has(item._id) ? " trash-item--selected" : ""}`}
              >
                <label className="trash-check">
                  <input
                    type="checkbox"
                    checked={selected.has(item._id)}
                    onChange={() => toggleSelect(item._id)}
                  />
                </label>
                <div className="trash-col--name">
                  <span className="trash-item__icon" style={{ color: `hsl(var(${meta.token}))` }}>
                    <Icon size={18} />
                  </span>
                  <span className="trash-item__title" title={item.title}>
                    {item.title}
                  </span>
                </div>
                <span className="trash-col--type">
                  <span className="trash-badge" style={{ background: `hsl(var(${meta.token}) / 0.09)`, color: `hsl(var(${meta.token}))` }}>
                    {meta.label}
                  </span>
                </span>
                <span className="trash-col--size">{formatBytes(item.fileSize)}</span>
                <span className="trash-col--date">{timeAgo(item.deletedAt)}</span>
                <div className="trash-col--actions">
                  <button
                    className="trash-btn trash-btn--sm trash-btn--outline"
                    title="Restore"
                    disabled={isActing}
                    onClick={() => handleRestore(item)}
                  >
                    {isActing ? <Loader className="spin" size={14} /> : <RotateCcw size={14} />}
                  </button>
                  <button
                    className="trash-btn trash-btn--sm trash-btn--danger-outline"
                    title="Delete permanently"
                    disabled={isActing}
                    onClick={() => setConfirmPurge(item)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Confirm Empty Modal */}
      {confirmEmpty && (
        <div className="trash-modal-overlay" onClick={() => setConfirmEmpty(false)}>
          <div className="trash-modal" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={32} className="trash-modal__icon" />
            <h3>Empty Trash?</h3>
            <p>
              This will permanently delete <strong>{items.length}</strong> item
              {items.length !== 1 ? "s" : ""} and free up{" "}
              <strong>{formatBytes(totalSize)}</strong> of storage. This cannot be undone.
            </p>
            <div className="trash-modal__actions">
              <button className="trash-btn trash-btn--outline" onClick={() => setConfirmEmpty(false)}>
                Cancel
              </button>
              <button className="trash-btn trash-btn--danger" onClick={handleEmptyTrash}>
                Delete All Permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Purge Single Item */}
      {confirmPurge && (
        <div className="trash-modal-overlay" onClick={() => setConfirmPurge(null)}>
          <div className="trash-modal" onClick={(e) => e.stopPropagation()}>
            <AlertTriangle size={32} className="trash-modal__icon" />
            <h3>Delete permanently?</h3>
            <p>
              <strong>{confirmPurge.title}</strong> ({formatBytes(confirmPurge.fileSize)}) will be
              permanently deleted. This cannot be undone.
            </p>
            <div className="trash-modal__actions">
              <button className="trash-btn trash-btn--outline" onClick={() => setConfirmPurge(null)}>
                Cancel
              </button>
              <button className="trash-btn trash-btn--danger" onClick={() => handlePurge(confirmPurge)}>
                Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrashPage;
